import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ControlsPage from "./pages/ControlsPage";
import ComplianceManagementPage from "./pages/ComplianceManagementPage";
import ComplianceDashboardPage from "./pages/ComplianceDashboardPage";
import AssessmentPage from "./pages/AssessmentPage";
import AuditLogPage from "./pages/AuditLogPage";
import IncidentRegisterPage from "./pages/IncidentRegisterPage";
import ImprovementActionsPage from "./pages/ImprovementActionsPage";
import M365IntegrationPage from "./pages/M365IntegrationPage";
import M365SettingsPage from "./pages/M365SettingsPage";
import AWSIntegrationPage from "./pages/AWSIntegrationPage";
import AWSSettingsPage from "./pages/AWSSettingsPage";
import GCPIntegrationPage from "./pages/GCPIntegrationPage";
import AzureIntegrationPage from "./pages/AzureIntegrationPage";
import AssetRegisterPage from "./pages/AssetRegisterPage";
import TrustCenterPage from "./pages/TrustCenterPage";
import PublicTrustCenterPage from "./pages/PublicTrustCenterPage";
import CustomDomainTrustCenterPage from "./pages/CustomDomainTrustCenterPage";
import OnboardingOffboardingPage from "./pages/OnboardingOffboardingPage";
import M365SetupGuidePage from "./pages/M365SetupGuidePage";
import PoliciesPage from "./pages/PoliciesPage";
import DocumentsPage from "./pages/DocumentsPage";
import SupplierRegisterPage from "./pages/SupplierRegisterPage";
import ThreatIntelligencePage from "./pages/ThreatIntelligencePage";
import GenericFrameworkPage from "./components/GenericFrameworkPage";
import ISO27001Page from "./pages/ISO27001Page";
import SOC2Page from "./pages/SOC2Page";
import ISO27001NonCompliantPage from "./pages/ISO27001NonCompliantPage";
import RecommendationsPage from "./pages/RecommendationsPage";
import GapAssessmentPage from "./pages/GapAssessmentPage";
import PolicyAckDashboard from "./pages/PolicyAckDashboard";
import PolicyAcknowledgePage from "./pages/PolicyAcknowledgePage";
import PolicyAcknowledgeBundlePage from "./pages/PolicyAcknowledgeBundlePage";
import FrameworkPage from "./pages/FrameworkPage";
import OrganizationSettingsPage from "./pages/OrganizationSettingsPage";
import ProfilePage from "./pages/ProfilePage";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import MasterControlLibraryPage from "./pages/MasterControlLibraryPage";
import FrameworkViewPage from "./pages/FrameworkViewPage";
import FrameworkRequirementsPage from "./pages/FrameworkRequirementsPage";
import GapEnginePage from "./pages/GapEnginePage";
import SoAPage from "./pages/SoAPage";
import RiskEnginePage from "./pages/RiskEnginePage";
import EvidenceEnginePage from "./pages/EvidenceEnginePage";
import ManagementReviewPage from "./pages/ManagementReviewPage";
import ReportsHubPage from "./pages/ReportsHubPage";
import MFASetupPage from "./pages/MFASetupPage";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import MFANotificationBanner from "./components/MFANotificationBanner";
import { Toaster } from "./components/ui/sonner";
import "@/App.css";

// List of known app domains (not custom domains)
const KNOWN_APP_DOMAINS = [
  'localhost',
  '127.0.0.1',
  'preview.emergentagent.com',
  'emergent.host',                 // Emergent production deployment domain
  'emergentagent.com',             // Emergent generic domain fallback
  'compliance.cyberforte.com.au',  // Main app domain
  'comply.cyberforte.com.au',      // Main app domain (alternate)
  'app.cyberforte.com.au',         // Reserved main app domain
];

// Helper function to check if current domain is a custom domain
const isCustomDomain = () => {
  const hostname = window.location.hostname;
  
  // Check if hostname exactly matches any known app domain
  for (const knownDomain of KNOWN_APP_DOMAINS) {
    if (hostname === knownDomain) {
      return false;
    }
    // Treat any subdomain of known Emergent hosts as an app domain (not a custom trust center)
    if (
      (knownDomain === 'preview.emergentagent.com' ||
        knownDomain === 'emergent.host' ||
        knownDomain === 'emergentagent.com') &&
      hostname.endsWith(`.${knownDomain}`)
    ) {
      return false;
    }
  }
  
  // If it's not an exact match to known domains, treat it as a custom domain
  // This allows trust.cyberforte.com.au, security.acme.com, etc.
  return true;
};


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// For production deployment, use relative URLs (same domain).
// For preview/development, use the configured BACKEND_URL.
// The rule is simple: if the frontend was served from any hosted
// domain (production or preview), the backend is on the same origin.
// Only fall back to the explicit BACKEND_URL when running locally.
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1';
  if (!isLocalDev) return '';           // same-origin in every hosted env
  return BACKEND_URL || '';
};

export const API = `${getApiBaseUrl()}/api`;

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingOrg, setViewingOrg] = useState(null); // For super admin to view specific org
  const [enabledFrameworks, setEnabledFrameworks] = useState([]);

  const fetchEnabledFrameworks = async (orgId = null) => {
    try {
      let endpoint = `${API}/tenant/frameworks`;
      
      // If super admin is viewing a specific org, use the admin endpoint
      if (orgId) {
        endpoint = `${API}/admin/tenants/${orgId}/frameworks`;
      }
      
      const response = await axios.get(endpoint);
      const enabled = response.data.frameworks
        ?.filter(f => f.enabled)
        ?.map(f => f.id) || [];
      setEnabledFrameworks(enabled);
    } catch (error) {
      console.error('Failed to fetch enabled frameworks:', error);
      // Default to all frameworks if fetch fails
      setEnabledFrameworks(['ISO27001', 'SOC2', 'Essential8', 'PCIDSS', 'HIPAA', 'ISO42001', 'SOCI']);
    }
  };

  const refreshEnabledFrameworks = () => {
    if (viewingOrg?.id) {
      fetchEnabledFrameworks(viewingOrg.id);
    } else {
      fetchEnabledFrameworks();
    }
  };

  useEffect(() => {
    // Check if user is logged in
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    const storedViewingOrg = localStorage.getItem("viewingOrg");
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      if (storedViewingOrg) {
        setViewingOrg(JSON.parse(storedViewingOrg));
      }
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
    setLoading(false);
  }, []);

  // Fetch enabled frameworks when token or viewingOrg changes
  useEffect(() => {
    if (token) {
      if (viewingOrg?.id) {
        fetchEnabledFrameworks(viewingOrg.id);
      } else {
        fetchEnabledFrameworks();
      }
    } else {
      setEnabledFrameworks([]);
    }
  }, [token, viewingOrg]);

  // Global axios interceptor — when a super admin has picked an org to
  // impersonate via `viewingOrg`, automatically forward `view_tenant_id` on
  // every backend request so SoA, Gap Assessment, MCL, and every other
  // tenant-scoped surface returns data for the impersonated org. Individual
  // pages that already append the param keep working (idempotent skip).
  useEffect(() => {
    const orgId = viewingOrg?.id;
    const interceptorId = axios.interceptors.request.use((config) => {
      if (!orgId) return config;
      const url = (config.url || '').toString();
      if (!url.startsWith(API)) return config; // only touch our backend
      if (url.includes('view_tenant_id=')) return config; // caller set it
      config.params = { ...(config.params || {}) };
      if (config.params.view_tenant_id === undefined) {
        config.params.view_tenant_id = orgId;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptorId);
  }, [viewingOrg]);

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setToken(token);
    setUser(userData);
    setViewingOrg(null); // Reset viewing org on login
    localStorage.removeItem("viewingOrg");
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const updateUser = (patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem("user", JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("viewingOrg");
    setToken(null);
    setUser(null);
    setViewingOrg(null);
    setEnabledFrameworks([]);
    delete axios.defaults.headers.common['Authorization'];
  };

  const selectOrg = (org) => {
    setViewingOrg(org);
    if (org) {
      localStorage.setItem("viewingOrg", JSON.stringify(org));
    } else {
      localStorage.removeItem("viewingOrg");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser, viewingOrg, selectOrg, isSuperAdmin, enabledFrameworks, refreshEnabledFrameworks }}>
      <ErrorBoundary>
      <BrowserRouter>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#1e293b',
              color: '#f1f5f9',
              border: '1px solid #334155',
            },
            className: 'toast-custom',
          }}
        />
        {/* If accessing via custom domain, show the Trust Center directly */}
        {isCustomDomain() ? (
          <Routes>
            <Route path="*" element={<CustomDomainTrustCenterPage />} />
          </Routes>
        ) : (
          <Routes>
            {/* Public Trust Center route - no authentication required */}
            <Route path="/trust/:slug" element={<PublicTrustCenterPage />} />
            <Route path="/policy-acknowledge/bundle/:token" element={<PolicyAcknowledgeBundlePage />} />
            <Route path="/policy-acknowledge/:token" element={<PolicyAcknowledgePage />} />
            <Route path="/login" element={!token ? <LoginPage /> : <Navigate to="/" />} />
            {/* Mandatory MFA enrolment — accessible only when signed in and
                MFA is NOT yet enabled. Once enabled, this route bounces home. */}
            <Route
              path="/mfa-setup"
              element={
                !token
                  ? <Navigate to="/login" />
                  : (user?.mfa_enabled ? <Navigate to="/" /> : <MFASetupPage />)
              }
            />
            <Route
              path="/"
              element={
                !token
                  ? <Navigate to="/login" />
                  : (!user?.mfa_enabled ? <Navigate to="/mfa-setup" /> : <Layout />)
              }
            >
              <Route index element={isSuperAdmin && !viewingOrg ? <SuperAdminDashboard /> : <DashboardPage />} />
              {/* MCL is admin-only — clients hitting the URL directly get bounced home. */}
              <Route
                path="master-control-library"
                element={user?.role === 'client' || user?.role === 'auditor' ? <Navigate to="/" replace /> : <MasterControlLibraryPage />}
              />
              <Route
                path="master-control-library/:controlId"
                element={user?.role === 'client' || user?.role === 'auditor' ? <Navigate to="/" replace /> : <MasterControlLibraryPage />}
              />
              <Route path="compliance" element={<ComplianceManagementPage />} />
              <Route path="compliance-automation" element={<ComplianceDashboardPage />} />
              {/* Framework views are now driven by the Master Control Library (Compliance Engine v2) */}
              <Route path="iso27001" element={<FrameworkViewPage />} />
              <Route path="soc2" element={<FrameworkViewPage />} />
              <Route path="essential8" element={<FrameworkViewPage />} />
              <Route path="frameworks/:routeKey/requirements" element={<FrameworkRequirementsPage />} />
              <Route path="gap-engine" element={<GapEnginePage />} />
              <Route path="soa" element={<SoAPage />} />
              <Route path="risk-engine" element={<RiskEnginePage />} />
              <Route path="evidence-engine" element={user?.role === 'client' || user?.role === 'auditor' ? <Navigate to="/" replace /> : <EvidenceEnginePage />} />
              <Route path="management-review" element={user?.role === 'client' || user?.role === 'auditor' ? <Navigate to="/" replace /> : <ManagementReviewPage />} />
              <Route path="reports" element={<ReportsHubPage />} />
              <Route path="incident-register" element={<IncidentRegisterPage />} />
              <Route path="improvement-actions" element={<ImprovementActionsPage />} />
              <Route path="policies" element={<PoliciesPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="supplier-register" element={<SupplierRegisterPage />} />
              <Route path="threat-intelligence" element={user?.role === 'client' || user?.role === 'auditor' ? <Navigate to="/" replace /> : <ThreatIntelligencePage />} />
              <Route path="framework" element={<FrameworkPage />} />
              <Route path="iso27001/non-compliant" element={<ISO27001NonCompliantPage />} />
              {/* Other frameworks still use GenericFrameworkPage */}
              <Route path="pci-dss" element={<GenericFrameworkPage frameworkKey="PCIDSS" />} />
              <Route path="hipaa" element={<GenericFrameworkPage frameworkKey="HIPAA" />} />
              <Route path="iso42001" element={<GenericFrameworkPage frameworkKey="ISO42001" />} />
              <Route path="soci" element={<GenericFrameworkPage frameworkKey="SOCI" />} />
              <Route path="recommendations" element={<RecommendationsPage />} />
              <Route path="gap-assessment" element={<GapAssessmentPage />} />
              <Route path="policy-acknowledgement" element={<PolicyAckDashboard />} />
              <Route path="audit-log" element={<AuditLogPage />} />
              <Route path="m365-integration" element={<M365IntegrationPage />} />
              <Route path="m365-settings" element={<M365SettingsPage />} />
              <Route path="aws-integration" element={<AWSIntegrationPage />} />
              <Route path="aws-settings" element={<AWSSettingsPage />} />
              <Route path="gcp-integration" element={<GCPIntegrationPage />} />
              <Route path="azure-integration" element={<AzureIntegrationPage />} />
              <Route path="asset-register" element={<AssetRegisterPage />} />
              <Route path="trust-center" element={<TrustCenterPage />} />
              <Route path="user-lifecycle" element={user?.role === 'client' || user?.role === 'auditor' ? <Navigate to="/" replace /> : <OnboardingOffboardingPage />} />
              <Route path="m365-setup-guide" element={<M365SetupGuidePage />} />
              <Route path="organization-settings" element={user?.role === 'client' || user?.role === 'auditor' ? <Navigate to="/" replace /> : <OrganizationSettingsPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>
          </Routes>
        )}
      </BrowserRouter>
      </ErrorBoundary>
    </AuthContext.Provider>
  );
}

export default App;